/**
 * TypeScript types for the Syllabus Progress Tracking System
 * 
 * These types mirror the backend Pydantic schemas and are used
 * throughout the frontend for type-safe progress tracking.
 */

// ============= ENUMS =============

export enum ProgressStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}

export enum MasteryLevel {
  NONE = 'NONE',
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
}

export enum ScheduleStatus {
  NOT_STARTED = 'NOT_STARTED',
  BEHIND = 'BEHIND',
  ON_TRACK = 'ON_TRACK',
  AHEAD = 'AHEAD',
  COMPLETED = 'COMPLETED',
}

export enum AssessmentType {
  QUIZ = 'QUIZ',
  TEST = 'TEST',
  PRACTICE = 'PRACTICE',
  EXAM = 'EXAM',
}

export enum QuestionType {
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
  TRUE_FALSE = 'TRUE_FALSE',
  SHORT_ANSWER = 'SHORT_ANSWER',
  LONG_ANSWER = 'LONG_ANSWER',
}

// ============= OBJECTIVE TYPES =============

export interface ObjectiveBase {
  id: string;
  objective_text: string;
  estimated_hours: number;
}

export interface ObjectiveProgress extends ObjectiveBase {
  status: ProgressStatus;
  mastery_level: MasteryLevel;
  last_interaction_at: string | null;
  completed_at: string | null;
}

// ============= SUBTOPIC TYPES =============

export interface SubtopicBase {
  id: string;
  name: string;
  description: string | null;
  estimated_hours: number;
}

export interface SubtopicProgress extends SubtopicBase {
  completion_percent: number;
  objectives_completed: number;
  total_objectives: number;
  objectives: ObjectiveProgress[];
}

// ============= TOPIC TYPES =============

export interface TopicBase {
  id: string;
  name: string;
  description: string | null;
  estimated_hours: number;
}

export interface TopicProgress extends TopicBase {
  completion_percent: number;
  status: ScheduleStatus;
  estimated_hours_remaining: number;
  objectives_completed: number;
  total_objectives: number;
  can_take_assessment: boolean;
  subtopics: SubtopicProgress[];
  direct_objectives: ObjectiveProgress[];
}

// ============= SCHEDULE TYPES =============

export interface ScheduleInfo {
  planned_start: string;
  planned_end: string;
  expected_completion_percent: number;
  days_remaining: number;
  is_overdue: boolean;
}

// ============= SUBJECT PROGRESS TYPES =============

export interface SubjectProgressSummary {
  subject_id: string;
  subject_name: string;
  grade: string | null;
  completion_percent: number;
  status: ScheduleStatus;
  estimated_hours_remaining: number;
  topics_completed: number;
  total_topics: number;
  objectives_completed: number;
  total_objectives: number;
  last_activity_at: string | null;
}

export interface SubjectProgressDetail extends SubjectProgressSummary {
  schedule: ScheduleInfo | null;
  status_message: string;
  topics: TopicProgress[];
}

// ============= OVERVIEW TYPES =============

export interface ProgressOverview {
  overall_completion_percent: number;
  overall_status: ScheduleStatus;
  estimated_total_hours_remaining: number;
  status_message: string;
  total_subjects: number;
  subjects_on_track: number;
  subjects_behind: number;
  subjects_ahead: number;
  subjects: SubjectProgressSummary[];
}

// ============= UPDATE TYPES =============

export interface ObjectiveProgressUpdate {
  status?: ProgressStatus;
  mastery_level?: MasteryLevel;
  notes?: string;
}

export interface ObjectiveProgressResponse {
  objective_id: string;
  status: ProgressStatus;
  mastery_level: MasteryLevel;
  updated_at: string;
  topic_completion_percent: number;
  subject_completion_percent: number;
}

// ============= ASSESSMENT TYPES =============

export interface AssessmentQuestion {
  id: string;
  question_text: string;
  question_type: QuestionType;
  options: Record<string, string> | null;
  points: number;
  sequence_order: number;
}

export interface AssessmentStartResponse {
  assessment_id: string;
  title: string;
  description: string | null;
  total_questions: number;
  time_limit_minutes: number | null;
  passing_score: number;
  questions: AssessmentQuestion[];
}

export interface AssessmentSubmission {
  answers: Record<string, string>;
  time_taken_seconds: number;
}

export interface WeakObjective {
  objective_id: string;
  objective_text: string;
  mastery_level: MasteryLevel;
}

export interface AssessmentResultResponse {
  result_id: string;
  score: number;
  passed: boolean;
  total_points: number;
  points_earned: number;
  time_taken_seconds: number;
  objectives_updated: Array<{
    objective_id: string;
    new_status: string;
    new_mastery: string;
    score: number;
  }>;
  weak_areas: WeakObjective[];
  feedback_message: string;
}

export interface AssessmentHistoryItem {
  result_id: string;
  assessment_id: string;
  assessment_title: string;
  score: number;
  passed: boolean;
  completed_at: string;
  time_taken_seconds: number | null;
}

// ============= ADMIN TYPES =============

export interface StudentProgressSummary {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  grade: string | null;
  overall_completion_percent: number;
  overall_status: ScheduleStatus;
  last_activity_at: string | null;
  subjects_count: number;
}

export interface StudentProgressForSubject {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  completion_percent: number;
  status: ScheduleStatus;
  topics_completed: number;
  total_topics: number;
  last_activity_at: string | null;
  estimated_hours_remaining: number;
}

export interface SubjectStudentsProgress {
  subject_id: string;
  subject_name: string;
  grade: string | null;
  total_students: number;
  average_completion: number;
  students_behind: number;
  students_on_track: number;
  students_ahead: number;
  students: StudentProgressForSubject[];
}

export interface SubjectSummary {
  subject_id: string;
  subject_name: string;
  grade: string | null;
  total_students: number;
  average_completion: number;
  students_behind: number;
  students_on_track: number;
  students_ahead: number;
}

export interface AdminProgressOverview {
  total_students: number;
  total_subjects: number;
  average_completion: number;
  students_behind: number;
  students_on_track: number;
  students_ahead: number;
  subject_summaries: SubjectSummary[];
}

// ============= UTILITY TYPES =============

/**
 * Helper type for status badge styling
 */
export interface StatusStyle {
  bgColor: string;
  textColor: string;
  borderColor: string;
  icon: string;
}

/**
 * Status styling configuration
 */
export const STATUS_STYLES: Record<ScheduleStatus, StatusStyle> = {
  [ScheduleStatus.NOT_STARTED]: {
    bgColor: 'bg-slate-500/10',
    textColor: 'text-slate-400',
    borderColor: 'border-slate-500/20',
    icon: '○',
  },
  [ScheduleStatus.BEHIND]: {
    bgColor: 'bg-red-500/10',
    textColor: 'text-red-400',
    borderColor: 'border-red-500/20',
    icon: '⚠',
  },
  [ScheduleStatus.ON_TRACK]: {
    bgColor: 'bg-emerald-500/10',
    textColor: 'text-emerald-400',
    borderColor: 'border-emerald-500/20',
    icon: '✓',
  },
  [ScheduleStatus.AHEAD]: {
    bgColor: 'bg-blue-500/10',
    textColor: 'text-blue-400',
    borderColor: 'border-blue-500/20',
    icon: '★',
  },
  [ScheduleStatus.COMPLETED]: {
    bgColor: 'bg-purple-500/10',
    textColor: 'text-purple-400',
    borderColor: 'border-purple-500/20',
    icon: '✓✓',
  },
};

/**
 * Mastery level styling configuration
 */
export const MASTERY_STYLES: Record<MasteryLevel, { color: string; label: string }> = {
  [MasteryLevel.NONE]: { color: 'text-slate-400', label: 'Not Started' },
  [MasteryLevel.LOW]: { color: 'text-red-400', label: 'Needs Work' },
  [MasteryLevel.MEDIUM]: { color: 'text-yellow-400', label: 'Developing' },
  [MasteryLevel.HIGH]: { color: 'text-emerald-400', label: 'Mastered' },
};

/**
 * Format percentage for display
 */
export function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}

/**
 * Format hours remaining for display
 */
export function formatHours(hours: number): string {
  if (hours < 1) {
    return `${Math.round(hours * 60)} min`;
  }
  if (hours === 1) {
    return '1 hour';
  }
  return `${hours.toFixed(1)} hours`;
}

/**
 * Get human-readable status label
 */
export function getStatusLabel(status: ScheduleStatus): string {
  switch (status) {
    case ScheduleStatus.NOT_STARTED:
      return 'Not Started';
    case ScheduleStatus.BEHIND:
      return 'Behind Schedule';
    case ScheduleStatus.ON_TRACK:
      return 'On Track';
    case ScheduleStatus.AHEAD:
      return 'Ahead of Schedule';
    case ScheduleStatus.COMPLETED:
      return 'Completed';
    default:
      return status;
  }
}

